import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { updateProfile } from 'firebase/auth';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { uploadAssetToR2 } from '../services/r2AssetUpload';
import TermsAndConditions from './Legal/TermsAndConditions';
import { useAuth } from '../context/AuthContext';
import { brand } from '../theme';
import { formatDate, formatDateTime } from '../utils/format';
import { isEditorOnlyRole } from '../utils/editorAccount';
import { performLogout } from '../utils/logout';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import KeyValueList from './ui/KeyValueList';

interface UserData {
  displayName?: string;
  email?: string;
  photoURL?: string;
  emailVerified?: boolean;
  creationTime?: string;
  lastSignInTime?: string;
}

type TermsKind = 'creator' | 'editor';

const PRIVACY_POLICY_URL = 'https://pithplay.com/privacy';

const SIGN_OUT_FAILED =
  "We couldn't sign you out completely. Close this browser tab to finish signing out.";

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const isE2EAuthBypassEnabled = () => {
  if (process.env.NODE_ENV === 'production') return false;
  if (process.env.REACT_APP_E2E_AUTH_BYPASS !== '1') return false;
  try {
    return window.localStorage.getItem('pithplay_e2e_auth_bypass') === '1';
  } catch {
    return false;
  }
};

/**
 * The account type as the account menu words it. The role used to be
 * hardcoded to "Creator", so an editor saw "Creator" on their own profile;
 * an unknown role says nothing rather than assert one the user may not have.
 */
const accountTypeFor = (role?: string | null): string => {
  if (role === 'superadmin') return 'Superadmin';
  if (role === 'creator,editor' || role === 'editor,creator') return 'Creator and editor';
  if (role === 'editor') return 'Editor';
  if (role === 'creator' || role === 'user') return 'Creator';
  return '';
};

const getInitials = (name?: string, email?: string) => {
  const source = (name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }
  return (email || 'U').trim().charAt(0).toUpperCase() || 'U';
};

/** One labelled row with its action on the right (below it on phones). */
const ActionRow: React.FC<{ title: string; description: string; action: React.ReactNode; first?: boolean }> = ({
  title,
  description,
  action,
  first = false,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: { xs: 1.25, sm: 2 },
      px: 2.5,
      py: 2,
      borderTop: first ? 0 : 1,
      borderColor: 'divider',
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="h3" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{action}</Box>
  </Box>
);

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, roleLoading } = useAuth();
  const role = authUser?.role;
  const editorOnly = isEditorOnlyRole(role);
  const hasEditorRole = (role || '').split(',').map((r) => r.trim()).includes('editor');
  const accountType = roleLoading ? '' : accountTypeFor(role);

  const [currentUserData, setCurrentUserData] = useState<UserData>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dialog and snackbar states
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [termsOpen, setTermsOpen] = useState<TermsKind | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    if (isE2EAuthBypassEnabled()) {
      const storedDisplayName = (() => {
        try {
          return window.localStorage.getItem('pithplay_e2e_display_name') || '';
        } catch {
          return '';
        }
      })();

      const storedPhotoURL = (() => {
        try {
          return window.localStorage.getItem('pithplay_e2e_photo_url') || '';
        } catch {
          return '';
        }
      })();

      const email = String(authUser?.email || '').trim();
      const fallbackName = storedDisplayName || String(authUser?.displayName || '') || (email ? email.split('@')[0] : 'E2E User');

      const userData: UserData = {
        displayName: fallbackName || 'E2E User',
        email,
        photoURL: storedPhotoURL || String(authUser?.photoURL || ''),
        emailVerified: true,
      };

      setCurrentUserData(userData);
      setDisplayName(userData.displayName || '');
      setLoading(false);
      return;
    }

    if (authUser) {
      const userData: UserData = {
        displayName: authUser.displayName || '',
        email: authUser.email || '',
        photoURL: authUser.photoURL || '',
        emailVerified: authUser.emailVerified,
        creationTime: authUser.metadata?.creationTime,
        lastSignInTime: authUser.metadata?.lastSignInTime,
      };

      setCurrentUserData(userData);
      setDisplayName(userData.displayName || '');
    }

    setLoading(false);
  }, [authUser]);

  const clearSelectedImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview('');
    setSelectedImage(null);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Let the same file be picked again after a cancel.
    event.target.value = '';
    if (file) {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setOpenImageDialog(true);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;

    setUploading(true);
    const user = authUser;

    if (isE2EAuthBypassEnabled()) {
      const photoURL = imagePreview || '';
      setCurrentUserData((prev) => ({ ...prev, photoURL }));
      try {
        window.localStorage.setItem('pithplay_e2e_photo_url', photoURL);
      } catch {
        // Ignore storage failures in test mode
      }

      setSnackbar({ open: true, message: 'Profile picture updated', severity: 'success' });
      setOpenImageDialog(false);
      // The preview URL is now the photo; don't revoke it.
      setImagePreview('');
      setSelectedImage(null);
      setUploading(false);
      return;
    }

    if (!user) {
      setSnackbar({ open: true, message: 'Your session has ended. Sign in again to change your photo.', severity: 'error' });
      setUploading(false);
      return;
    }

    try {
      const token = await user.getIdToken(true);
      const stored = await uploadAssetToR2(selectedImage, 'profile-image', token);
      const downloadURL = stored.url;

      await updateProfile(user, { photoURL: downloadURL });

      setCurrentUserData((prev) => ({ ...prev, photoURL: downloadURL }));
      setSnackbar({ open: true, message: 'Profile picture updated', severity: 'success' });
      setOpenImageDialog(false);
      clearSelectedImage();
    } catch (error) {
      console.error('Error uploading image:', error);
      setSnackbar({ open: true, message: "We couldn't update your photo. Try again.", severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (saving) return;
    const nextName = displayName.trim();
    if (!nextName) return;

    setSaving(true);
    const user = authUser;

    if (isE2EAuthBypassEnabled()) {
      try {
        window.localStorage.setItem('pithplay_e2e_display_name', nextName);
      } catch {
        // Ignore storage failures in test mode
      }

      setCurrentUserData((prev) => ({ ...prev, displayName: nextName }));
      setEditMode(false);
      setSnackbar({ open: true, message: 'Profile updated', severity: 'success' });
      setSaving(false);
      return;
    }

    if (!user) {
      setSnackbar({ open: true, message: 'Your session has ended. Sign in again to update your profile.', severity: 'error' });
      setSaving(false);
      return;
    }

    try {
      await updateProfile(user, { displayName: nextName });

      setCurrentUserData((prev) => ({ ...prev, displayName: nextName }));
      setEditMode(false);
      setSnackbar({ open: true, message: 'Profile updated', severity: 'success' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({ open: true, message: "We couldn't update your profile. Try again.", severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const closeEditDialog = () => {
    setEditMode(false);
    setDisplayName(currentUserData.displayName || '');
  };

  const closeImageDialog = () => {
    setOpenImageDialog(false);
    clearSelectedImage();
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      // Signs out every open tab and clears cached GraphQL results, the same
      // as Sign out in the account menu.
      await performLogout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      setSnackbar({ open: true, message: SIGN_OUT_FAILED, severity: 'error' });
      setSigningOut(false);
    }
  };

  const header = <PageHeader title="Profile" subtitle="Your name, photo and account details." />;

  if (loading) {
    return (
      <PageContainer width="narrow">
        {header}
        <Section title="Your details">
          <Box role="status" aria-label="Loading profile">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={64} height={64} />
              <Skeleton width={120} />
            </Box>
            <Skeleton width="60%" />
            <Skeleton width="45%" />
            <Skeleton width="50%" />
          </Box>
        </Section>
      </PageContainer>
    );
  }

  const name = currentUserData.displayName?.trim() || '';

  return (
    <PageContainer width="narrow">
      {header}

      <Box sx={{ display: 'grid', gap: 3 }}>
        <Section
          title="Your details"
          action={
            <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} onClick={() => setEditMode(true)}>
              Edit profile
            </Button>
          }
        >
          <KeyValueList
            items={[
              {
                label: 'Photo',
                fullWidth: true,
                value: (
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    <Avatar
                      src={currentUserData.photoURL || undefined}
                      alt=""
                      sx={{
                        width: 56,
                        height: 56,
                        fontSize: '1.125rem',
                        fontWeight: 700,
                        bgcolor: 'grey.200',
                        color: brand.slate,
                        border: `1px solid ${brand.border}`,
                      }}
                    >
                      {getInitials(name, currentUserData.email)}
                    </Avatar>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<PhotoCameraOutlinedIcon />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change photo
                    </Button>
                    <input
                      id="image-upload"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      hidden
                      aria-label="Choose a profile photo"
                    />
                  </Box>
                ),
              },
              { label: 'Display name', value: name },
              {
                label: 'Email',
                value: currentUserData.email ? (
                  <>
                    {currentUserData.email}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
                      Can&apos;t be changed
                    </Typography>
                  </>
                ) : null,
              },
              { label: 'Account type', value: accountType },
              { label: 'Member since', value: formatDate(currentUserData.creationTime) },
              { label: 'Last sign-in', value: formatDateTime(currentUserData.lastSignInTime) },
            ]}
          />
        </Section>

        <Section title="Account" disablePadding>
          <ActionRow
            first
            title="Password"
            description="We'll email you a link to choose a new password."
            action={
              <Button variant="outlined" size="small" component={RouterLink} to="/forgot-password">
                Change password
              </Button>
            }
          />
          {!roleLoading && !editorOnly && (
            <ActionRow
              title="Plan and billing"
              description="Your plan, storage and invoices."
              action={
                <Button variant="outlined" size="small" component={RouterLink} to="/billing">
                  Open billing
                </Button>
              }
            />
          )}
          <ActionRow
            title="Sign out"
            description="Sign out of PithPlay in this browser, including other open tabs."
            action={
              <Button
                variant="outlined"
                size="small"
                onClick={handleSignOut}
                disabled={signingOut}
                startIcon={signingOut ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {signingOut ? 'Signing out…' : 'Sign out'}
              </Button>
            }
          />
        </Section>

        <Box component="nav" aria-label="Legal" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1 }}>
          {!editorOnly && (
            <Link
              component="button"
              type="button"
              variant="body2"
              color="text.secondary"
              underline="always"
              onClick={() => setTermsOpen('creator')}
            >
              Terms and conditions
            </Link>
          )}
          {hasEditorRole && (
            <Link
              component="button"
              type="button"
              variant="body2"
              color="text.secondary"
              underline="always"
              onClick={() => setTermsOpen('editor')}
            >
              Editor terms
            </Link>
          )}
          <Link
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            color="text.secondary"
            underline="always"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            Privacy policy
            <OpenInNewOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
            <Box component="span" sx={visuallyHidden}>
              (opens in a new tab)
            </Box>
          </Link>
        </Box>
      </Box>

      {/* Edit profile */}
      <Dialog open={editMode} onClose={closeEditDialog} maxWidth="xs" fullWidth aria-labelledby="edit-profile-title">
        <form onSubmit={handleSaveProfile} noValidate>
          <DialogTitle id="edit-profile-title">Edit profile</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="display-name"
              label="Display name"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              helperText="How your name appears to the editors and creators you work with."
              inputProps={{ maxLength: 80 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={closeEditDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !displayName.trim()}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Profile picture */}
      <Dialog open={openImageDialog} onClose={closeImageDialog} maxWidth="xs" fullWidth aria-labelledby="profile-photo-title">
        <DialogTitle id="profile-photo-title">Update profile picture</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {imagePreview && (
            <Avatar
              src={imagePreview}
              alt="Preview of your new profile picture"
              sx={{ width: 112, height: 112, mx: 'auto', mb: 2, border: `1px solid ${brand.border}` }}
            />
          )}
          <DialogContentText variant="body2">This photo appears on your account across PithPlay.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeImageDialog}>Cancel</Button>
          <Button
            onClick={handleImageUpload}
            variant="contained"
            disabled={uploading || !selectedImage}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {uploading ? 'Uploading…' : 'Update picture'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 8000 : 4000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setSnackbar((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <TermsAndConditions
        open={termsOpen !== null}
        onClose={() => setTermsOpen(null)}
        showAcceptButton={false}
        termsFile={termsOpen === 'editor' ? 'EDITOR_TERMS_AND_CONDITIONS.md' : 'TERMS_AND_CONDITIONS.md'}
      />
    </PageContainer>
  );
};

export default Profile;
